import React, { useMemo, useState } from "react";

function isFirefox() {
  if (typeof navigator === "undefined") return false;
  // "InstallTrigger" is Firefox-specific, but UA check helps with messaging.
  return /firefox/i.test(navigator.userAgent);
}

function getXpiUrl() {
  return (
    process.env.REACT_APP_FIREFOX_XPI_URL ||
    process.env.REACT_APP_EXTENSION_XPI_URL ||
    ""
  );
}

export default function FirefoxExtensionInstall() {
  const xpiUrl = useMemo(() => getXpiUrl(), []);
  const [status, setStatus] = useState("");

  const canUseInstallTrigger =
    typeof window !== "undefined" &&
    typeof window.InstallTrigger !== "undefined" &&
    typeof window.InstallTrigger.install === "function";

  const onInstall = async () => {
    setStatus("");

    if (!xpiUrl) {
      setStatus(
        "Missing XPI URL. Set REACT_APP_FIREFOX_XPI_URL in your environment and redeploy."
      );
      return;
    }

    if (!isFirefox()) {
      window.open(xpiUrl, "_blank", "noopener,noreferrer");
      setStatus("Opened the XPI download in a new tab (Firefox required to install).");
      return;
    }

    if (!canUseInstallTrigger) {
      window.location.href = xpiUrl;
      return;
    }

    try {
      setStatus("Requesting install… check the Firefox prompt.");
      await new Promise((resolve, reject) => {
        window.InstallTrigger.install(
          {
            "Cynet Labs Extension": xpiUrl,
          },
          () => resolve(),
          (err) => reject(err)
        );
      });
      setStatus("Install requested. If Firefox blocked it, use the direct download link below.");
    } catch (e) {
      setStatus(
        "Install failed or was blocked. Use the direct download link below and confirm the Firefox prompt."
      );
    }
  };

  return (
    <div className="min-h-[70vh] px-4 py-10">
      <div className="max-w-2xl mx-auto bg-white/80 dark:bg-gray-800/70 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-sm backdrop-blur-md p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Install the Firefox Extension
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          Firefox will always ask for confirmation. This button can only start the
          install flow — you still need to approve it in the browser.
        </p>

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <button
            onClick={onInstall}
            className="inline-flex items-center justify-center rounded-xl px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition"
          >
            Install on Firefox
          </button>

          {xpiUrl ? (
            <a
              href={xpiUrl}
              className="inline-flex items-center justify-center rounded-xl px-4 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 font-semibold transition"
            >
              Direct download (.xpi)
            </a>
          ) : (
            <span className="inline-flex items-center text-sm text-amber-700 dark:text-amber-300">
              XPI URL not configured
            </span>
          )}
        </div>

        {!!status && (
          <div className="mt-4 text-sm text-gray-700 dark:text-gray-200">
            {status}
          </div>
        )}

        <div className="mt-6 text-sm text-gray-600 dark:text-gray-300 space-y-2">
          <p className="font-semibold text-gray-800 dark:text-gray-100">
            GCS hosting requirements
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              Set the object metadata <code className="font-mono">Content-Type</code>{" "}
              to <code className="font-mono">application/x-xpinstall</code>.
            </li>
            <li>Serve over HTTPS (the default for <code className="font-mono">storage.googleapis.com</code>).</li>
            <li>
              On regular Firefox, the extension typically must be Mozilla-signed (or
              installed via enterprise policy / dev environment).
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

